import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { requestOrDelete } from "@/lib/approval";
import VehicleSearchSelect from "@/components/VehicleSearchSelect";
import { PageHeader, Surface, Badge, Button, Modal, Input, Select, NewButton } from "@/components/ui-kit";
import { formatDate } from "@/lib/format";
import { ClockCounterClockwise, ArrowRight, UploadSimple, FileText, Trash, ArrowsLeftRight, X, Eye } from "@phosphor-icons/react";
import { downloadCsv, downloadMultiSectionCsv, printElement, printSections, regSort } from "@/lib/csv";
import ReportToolbar, { ZoomScope, ZOOM_DEFAULT } from "@/components/ReportToolbar";

const STATUSES = ["in_stock", "fitted", "scrapped", "sold"];

const empty = {
    serial_number: "", brand: "", model: "", voltage: 12, capacity_ah: 100,
    purchase_cost: 0, purchase_date: "", warranty_months: 12, expected_life_years: 3,
    status: "in_stock", vehicle_id: "",
};

// Compute warranty status from purchase_date + warranty_months
function warrantyStatus(b) {
    if (!b?.purchase_date || !b?.warranty_months) return null;
    const start = new Date(b.purchase_date);
    if (isNaN(start.getTime())) return null;
    const end = new Date(start);
    end.setMonth(end.getMonth() + Number(b.warranty_months || 0));
    const now = new Date();
    const daysLeft = Math.floor((end - now) / 86400000);
    return { end, daysLeft, active: daysLeft >= 0 };
}

export default function Batteries() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);

    const [search, setSearch] = useState("");
    const [filterVehicle, setFilterVehicle] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterBrand, setFilterBrand] = useState("");
    const [filterVoltage, setFilterVoltage] = useState("");
    const [filterWarranty, setFilterWarranty] = useState(""); // active | expired | none
    const tableRef = useRef(null);

    const [assignFor, setAssignFor] = useState(null);
    const [assignForm, setAssignForm] = useState({
        vehicle_id: "",
        odometer_at_install: 0,
        odometer_old_vehicle_at_remove: 0,
        notes: "",
    });

    const [historyFor, setHistoryFor] = useState(null);
    const [history, setHistory] = useState([]);

    const [docsFor, setDocsFor] = useState(null);
    const [docs, setDocs] = useState([]);
    const [docCounts, setDocCounts] = useState({});
    const fileInputRef = useRef(null);

    // Server-side pagination state
    const PAGE_SIZE = 20;
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [busy, setBusy] = useState(false);
    const [zoom, setZoom] = useState(ZOOM_DEFAULT);

    const load = useCallback(async (targetPage) => {
        const tp = targetPage ?? 1;
        setBusy(true);
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.set("q", search.trim());
            if (filterStatus) params.set("status", filterStatus);
            if (filterBrand) params.set("brand", filterBrand);
            if (filterVehicle) params.set("vehicle_id", filterVehicle);
            params.set("page", String(tp));
            params.set("page_size", String(PAGE_SIZE));
            const r = await api.get(`/batteries?${params.toString()}`);
            const payload = r.data || {};
            setList(payload.items || []);
            setTotal(payload.total ?? 0);
            setPage(payload.page ?? tp);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load batteries");
        } finally {
            setBusy(false);
        }
    }, [search, filterStatus, filterBrand, filterVehicle]);

    const loadDocCounts = () => api.get("/batteries/-documents/counts").then((r) => setDocCounts(r.data || {})).catch(() => {});
    useEffect(() => {
        load(1);
        loadDocCounts();
        api.get("/vehicles").then((r) => setVehicles(r.data));
    }, [load]);

    // Refetch when filters change
    useEffect(() => {
        setPage(1);
        load(1);
    }, [search, filterStatus, filterBrand, filterVehicle, load]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const save = async () => {
        try {
            const payload = {
                ...form,
                voltage: Number(form.voltage), capacity_ah: Number(form.capacity_ah),
                purchase_cost: Number(form.purchase_cost),
                warranty_months: Number(form.warranty_months),
                expected_life_years: form.expected_life_years === "" ? null : Number(form.expected_life_years),
                purchase_date: form.purchase_date || null,
                vehicle_id: form.vehicle_id || null,
            };
            if (editing) await api.put(`/batteries/${editing}`, payload);
            else await api.post("/batteries", payload);
            toast.success("Saved");
            setOpen(false); setEditing(null); setForm(empty); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const edit = (b) => { setForm({ ...empty, ...b, vehicle_id: b.vehicle_id || "", expected_life_years: b.expected_life_years ?? "", purchase_date: b.purchase_date || "" }); setEditing(b.id); setOpen(true); };
    const del = async (b) => {
        const r = await requestOrDelete("battery", b.id, `battery ${b.serial_number}`, user?.role);
        if (r.ok && !r.requested) load(page);
    };

    const openAssign = (b) => {
        setAssignFor(b);
        // For switch: pre-fill old-vehicle odometer with current vehicle's odometer
        const currentVehicle = b.vehicle_id ? vehicles.find((v) => v.id === b.vehicle_id) : null;
        setAssignForm({
            vehicle_id: "",
            odometer_at_install: 0,
            odometer_old_vehicle_at_remove: currentVehicle?.odometer || 0,
            notes: "",
        });
    };

    const submitAssign = async () => {
        if (!assignForm.vehicle_id) { toast.error("Pick a vehicle"); return; }
        try {
            const payload = {
                vehicle_id: assignForm.vehicle_id,
                odometer_at_install: Number(assignForm.odometer_at_install) || 0,
                notes: assignForm.notes,
            };
            if (assignFor.status === "fitted") {
                payload.odometer_old_vehicle_at_remove = Number(assignForm.odometer_old_vehicle_at_remove) || 0;
            }
            await api.post(`/batteries/${assignFor.id}/assign`, payload);
            toast.success(assignFor.status === "fitted" ? "Switched to new vehicle" : "Battery assigned");
            setAssignFor(null); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const removeFromVehicle = async (b) => {
        const odo = window.prompt("Current odometer reading?", "0");
        if (odo === null) return;
        try {
            await api.post(`/batteries/${b.id}/remove`, { odometer: Number(odo) || 0, reason: "remove" });
            toast.success("Battery removed"); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const showHistory = async (b) => {
        const r = await api.get(`/batteries/${b.id}/history`);
        setHistory(r.data); setHistoryFor(b);
    };

    // ---------- Sell battery ----------
    const emptySale = { buyer_name: "", sale_date: new Date().toISOString().slice(0, 10), sale_amount_inr: "", buyer_phone: "", buyer_address: "", notes: "" };
    const [sellFor, setSellFor] = useState(null);
    const [sellForm, setSellForm] = useState(emptySale);
    const [sellSubmitting, setSellSubmitting] = useState(false);
    const openSell = (b) => { setSellFor(b); setSellForm(emptySale); };
    const submitSell = async () => {
        if (!sellFor) return;
        if (!sellForm.buyer_name.trim()) { toast.error("Buyer name is required"); return; }
        const amt = Number(sellForm.sale_amount_inr);
        if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a valid sale amount"); return; }
        setSellSubmitting(true);
        const isAdmin = user?.role === "admin";
        const saleInfo = {
            buyer_name: sellForm.buyer_name.trim(),
            sale_date: sellForm.sale_date || null,
            sale_amount_inr: amt,
            buyer_phone: sellForm.buyer_phone.trim() || null,
            buyer_address: sellForm.buyer_address.trim() || null,
            notes: sellForm.notes.trim() || null,
        };
        try {
            if (isAdmin) {
                await api.post(`/batteries/${sellFor.id}/sell`, saleInfo);
                toast.success(`Battery sold to ${sellForm.buyer_name.trim()}`);
            } else {
                await api.post(`/approvals`, {
                    entity_type: "battery",
                    entity_id: sellFor.id,
                    action: "sell",
                    reason: sellForm.notes.trim() || null,
                    sale_info: saleInfo,
                });
                toast.success("Sale request submitted for admin approval.");
            }
            setSellFor(null); load(page);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail, e) || "Sale failed");
        } finally { setSellSubmitting(false); }
    };
    const undoSale = async (b) => {
        if (!window.confirm(`Undo sale of battery ${b.serial_number}? It will return to 'in_stock'.`)) return;
        try {
            await api.post(`/batteries/${b.id}/undo-sale`);
            toast.success("Sale reversed"); load(page);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail, e) || "Undo failed");
        }
    };

    const openDocs = async (b) => {
        const r = await api.get(`/batteries/${b.id}/documents`);
        setDocs(r.data); setDocsFor(b);
    };

    const uploadDocs = async (files, label) => {
        if (!files?.length || !docsFor) return;
        const tid = toast.loading(`Uploading ${files.length} file(s)…`);
        let okCount = 0, failCount = 0;
        for (const f of files) {
            const fd = new FormData(); fd.append("file", f);
            try {
                await api.post(
                    `/upload?entity_type=battery&entity_id=${docsFor.id}&label=${encodeURIComponent(label || f.name)}`,
                    fd, { headers: { "Content-Type": "multipart/form-data" } },
                );
                okCount += 1;
            } catch (e) {
                failCount += 1;
            }
        }
        if (failCount === 0) toast.success(`Uploaded ${okCount} file(s)`, { id: tid });
        else if (okCount === 0) toast.error("Upload failed", { id: tid });
        else toast.success(`Uploaded ${okCount} · ${failCount} failed`, { id: tid });
        const r = await api.get(`/batteries/${docsFor.id}/documents`);
        setDocs(r.data);
        loadDocCounts();
    };

    const delDoc = async (fid) => {
        if (!window.confirm("Remove this document?")) return;
        try {
            await api.delete(`/files/${fid}`);
            toast.success("Document removed");
            const r = await api.get(`/batteries/${docsFor.id}/documents`);
            setDocs(r.data);
            loadDocCounts();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not remove document");
        }
    };

    const vehMap = Object.fromEntries(vehicles.map((v) => [v.id, v]));
    // Memoize the alphanumerically-sorted vehicle list once per `vehicles` change
    // so the dropdown doesn't re-sort on every keystroke / filter update.
    const sortedVehicles = useMemo(() => [...vehicles].sort(regSort), [vehicles]);

    const brands = useMemo(
        () => Array.from(new Set(list.map((b) => b.brand).filter(Boolean))).sort(),
        [list]
    );
    const voltages = useMemo(
        () => Array.from(new Set(list.map((b) => b.voltage).filter(Boolean))).sort((a, b) => a - b),
        [list]
    );

    // Server-side filters are applied; voltage + warranty filters remain client-side
    // because the backend list endpoint doesn't index them.
    const filtered = list.filter((b) => {
        if (filterVoltage && String(b.voltage) !== String(filterVoltage)) return false;
        if (filterWarranty) {
            const w = warrantyStatus(b);
            if (filterWarranty === "active" && (!w || !w.active)) return false;
            if (filterWarranty === "expired" && (!w || w.active)) return false;
            if (filterWarranty === "none" && w !== null) return false;
        }
        return true;
    });

    const clearFilters = () => {
        setSearch(""); setFilterVehicle(""); setFilterStatus("");
        setFilterBrand(""); setFilterVoltage(""); setFilterWarranty("");
    };
    const hasFilters = search || filterVehicle || filterStatus || filterBrand || filterVoltage || filterWarranty;

    // Fetch all rows matching the active filters (used by CSV/PDF exports)
    const fetchAllFiltered = useCallback(async () => {
        const params = new URLSearchParams();
        if (search.trim()) params.set("q", search.trim());
        if (filterStatus) params.set("status", filterStatus);
        if (filterBrand) params.set("brand", filterBrand);
        if (filterVehicle) params.set("vehicle_id", filterVehicle);
        const r = await api.get(`/batteries${params.toString() ? `?${params.toString()}` : ""}`);
        let rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        if (filterVoltage) rows = rows.filter((b) => String(b.voltage) === String(filterVoltage));
        if (filterWarranty) {
            rows = rows.filter((b) => {
                const w = warrantyStatus(b);
                if (filterWarranty === "active") return w && w.active;
                if (filterWarranty === "expired") return w && !w.active;
                return w === null;
            });
        }
        return rows;
    }, [search, filterStatus, filterBrand, filterVehicle, filterVoltage, filterWarranty]);

    const fetchHistoryFor = async (rows) => {
        const out = [];
        for (const b of rows) {
            try {
                const r = await api.get(`/batteries/${b.id}/history`);
                for (const h of r.data) out.push({ serial: b.serial_number, brand: b.brand, ...h });
            } catch (err) { console.warn("Battery history fetch failed:", err); }
        }
        return out;
    };

    const exportCsv = async () => {
        const tid = toast.loading("Compiling export…");
        const allRows = await fetchAllFiltered();
        const history = await fetchHistoryFor(allRows);
        downloadMultiSectionCsv(`batteries-${new Date().toISOString().slice(0, 10)}.csv`, [
            {
                title: `Batteries (${allRows.length} record(s))`,
                rows: allRows,
                columns: [
                    ["SERIAL", "serial_number"], ["BRAND", "brand"], ["MODEL", "model"],
                    ["VOLTAGE (V)", "voltage"], ["CAPACITY (Ah)", "capacity_ah"],
                    ["PURCHASE COST (INR)", "purchase_cost"],
                    ["PURCHASE DATE", (b) => formatDate(b.purchase_date)],
                    ["WARRANTY (MONTHS)", "warranty_months"],
                    ["WARRANTY STATUS", (b) => {
                        const w = warrantyStatus(b);
                        if (!w) return "—";
                        return w.active ? `${w.daysLeft}d left` : `Expired ${-w.daysLeft}d ago`;
                    }],
                    ["EXPECTED LIFE (YRS)", "expected_life_years"],
                    ["VEHICLE", (b) => vehMap[b.vehicle_id]?.reg_number || ""],
                    ["STATUS", "status"],
                ],
            },
            {
                title: `Assignment history (${history.length} entries)`,
                rows: history,
                columns: [
                    ["SERIAL", "serial"], ["BRAND", "brand"],
                    ["VEHICLE", "vehicle_reg"],
                    ["ASSIGNED AT", (h) => formatDate(h.assigned_at)],
                    ["REMOVED AT", (h) => h.removed_at ? formatDate(h.removed_at) : "—"],
                    ["ODO @ INSTALL", "odometer_at_install"],
                    ["ODO @ REMOVE", (h) => h.odometer_at_remove ?? "—"],
                    ["KM RUN", "km_run"],
                    ["CUMULATIVE KM", "cumulative_km"],
                    ["REASON", "reason"],
                ],
            },
        ]);
        toast.success(`Exported · ${allRows.length} batteries + ${history.length} history rows`, { id: tid });
    };

    const exportPdf = async () => {
        const tid = toast.loading("Compiling export…");
        const allRows = await fetchAllFiltered();
        const history = await fetchHistoryFor(allRows);
        printSections(`EAZYFLEET · Batteries · ${allRows.length} record(s)`, [
            {
                title: "Batteries",
                rows: allRows,
                columns: [
                    { label: "SERIAL", key: "serial_number" },
                    { label: "BRAND", key: (b) => `${b.brand || ""} ${b.model || ""}` },
                    { label: "V / Ah", key: (b) => `${b.voltage}V / ${b.capacity_ah}Ah` },
                    { label: "VEHICLE", key: (b) => vehMap[b.vehicle_id]?.reg_number || "" },
                    { label: "WARRANTY", key: (b) => { const w = warrantyStatus(b); return w ? (w.active ? `${w.daysLeft}d left` : `Exp ${-w.daysLeft}d`) : "—"; } },
                    { label: "STATUS", key: "status" },
                ],
            },
            {
                title: `Assignment history (${history.length})`,
                rows: history,
                columns: [
                    { label: "SERIAL", key: "serial" },
                    { label: "VEHICLE", key: "vehicle_reg" },
                    { label: "ASSIGNED", key: (h) => formatDate(h.assigned_at) },
                    { label: "REMOVED", key: (h) => h.removed_at ? formatDate(h.removed_at) : "—" },
                    { label: "KM RUN", key: "km_run" },
                    { label: "CUMUL KM", key: "cumulative_km" },
                    { label: "REASON", key: "reason" },
                ],
            },
        ]);
        toast.success("Print dialog opened", { id: tid });
    };

    return (
        <div className="p-6 lg:p-8 animate-fade-in">
            <PageHeader
                eyebrow="ASSETS"
                title="Batteries"
                subtitle={`${list.length} batteries tracked · install / switch / warranty`}
                action={<NewButton onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} label="Add battery" testid="add-battery-btn" />}
            />

            {/* Search & filter */}
            <Surface className="px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
                <input
                    type="text"
                    placeholder="Search by serial / brand / vehicle…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="px-3 py-2 bg-white border border-fleet-border rounded-md text-sm w-64 focus:outline-none focus:border-fleet-ink"
                    data-testid="battery-search"
                />
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="battery-filter-status">
                    <option value="">All statuses</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
                <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="battery-filter-brand">
                    <option value="">All brands</option>
                    {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                <select value={filterVoltage} onChange={(e) => setFilterVoltage(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="battery-filter-voltage">
                    <option value="">All voltages</option>
                    {voltages.map((v) => <option key={v} value={v}>{v}V</option>)}
                </select>
                <select value={filterVehicle} onChange={(e) => setFilterVehicle(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="battery-filter-vehicle">
                    <option value="">All vehicles</option>
                    {sortedVehicles.map((v) => (
                        <option key={v.id} value={v.id}>{v.reg_number}</option>
                    ))}
                </select>
                <select value={filterWarranty} onChange={(e) => setFilterWarranty(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="battery-filter-warranty">
                    <option value="">Any warranty</option>
                    <option value="active">Warranty active</option>
                    <option value="expired">Warranty expired</option>
                    <option value="none">No purchase date</option>
                </select>
                {hasFilters && (
                    <button onClick={clearFilters} className="text-xs text-fleet-inkSoft hover:text-fleet-ink underline flex items-center gap-1" data-testid="battery-clear-filters">
                        <X size={12} /> Clear
                    </button>
                )}
            </Surface>

            <Surface className="px-4 py-2 mb-4">
                <ReportToolbar
                    page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE}
                    onPageChange={(p) => { setPage(p); load(p); }}
                    onRefresh={() => load(page)}
                    zoom={zoom} onZoomChange={setZoom}
                    onExportCsv={exportCsv}
                    onExportPdf={exportPdf}
                    busy={busy}
                />
            </Surface>

            <Surface className="overflow-hidden">
                <ZoomScope zoom={zoom}>
                <table className="w-full text-sm" ref={tableRef}>
                    <thead className="bg-slate-50"><tr className="text-left">
                        {["SERIAL", "BRAND", "VOLT", "AH", "VEHICLE", "WARRANTY", "LIFE", "STATUS", ""].map((h, i, arr) => <th key={h} className={`label-mono px-5 py-3 font-normal${i === arr.length - 1 ? " no-print" : ""}`}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {filtered.map((b) => {
                            const w = warrantyStatus(b);
                            return (
                            <tr key={b.id} className="border-t border-fleet-border data-grid-row">
                                <td className="px-5 py-3 font-mono">{b.serial_number}</td>
                                <td className="px-5 py-3">{b.brand} {b.model}</td>
                                <td className="px-5 py-3 font-mono">{b.voltage}V</td>
                                <td className="px-5 py-3 font-mono">{b.capacity_ah}Ah</td>
                                <td className="px-5 py-3 font-mono text-xs">{vehMap[b.vehicle_id]?.reg_number || "—"}</td>
                                <td className="px-5 py-3" data-testid={`bat-warranty-${b.id}`}>
                                    {w ? (
                                        <Badge variant={w.active ? "healthy" : "critical"}>
                                            {w.active ? `${w.daysLeft}d LEFT` : `EXPIRED ${-w.daysLeft}d`}
                                        </Badge>
                                    ) : (
                                        <span className="text-xs text-fleet-inkMuted">{b.warranty_months}m · no purchase date</span>
                                    )}
                                </td>
                                <td className="px-5 py-3 font-mono">{b.expected_life_years ? `${b.expected_life_years}y` : "—"}</td>
                                <td className="px-5 py-3"><Badge variant={b.status}>{b.status}</Badge></td>
                                <td className="px-5 py-3 text-right whitespace-nowrap no-print">
                                    <Button variant="ghost" size="sm" onClick={() => showHistory(b)} data-testid={`bat-history-${b.id}`}><ClockCounterClockwise size={14} /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => openDocs(b)} data-testid={`bat-docs-${b.id}`}>
                                        <FileText size={14} />
                                        {(docCounts[b.id] || 0) > 0 && (
                                            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-fleet-ink text-white text-[10px] font-mono leading-none">
                                                {docCounts[b.id]}
                                            </span>
                                        )}
                                    </Button>
                                    {b.status !== "scrapped" && b.status !== "sold" && (
                                        <Button variant="ghost" size="sm" onClick={() => openAssign(b)} data-testid={`bat-assign-${b.id}`}>
                                            <ArrowsLeftRight size={14} /> {b.status === "fitted" ? "Switch" : "Assign"}
                                        </Button>
                                    )}
                                    {b.status === "fitted" && <Button variant="ghost" size="sm" onClick={() => removeFromVehicle(b)} data-testid={`remove-bat-${b.id}`}>Remove</Button>}
                                    {b.status !== "sold" && b.status !== "scrapped" && <Button variant="ghost" size="sm" onClick={() => openSell(b)} data-testid={`sell-bat-${b.id}`}>{user?.role === "admin" ? "Sell" : "Request sale"}</Button>}
                                    {b.status === "sold" && <Button variant="ghost" size="sm" onClick={() => undoSale(b)} data-testid={`undo-sale-bat-${b.id}`}>Undo sale</Button>}
                                    {b.status !== "sold" && <Button variant="ghost" size="sm" onClick={() => edit(b)} data-testid={`edit-bat-${b.id}`}>Edit</Button>}
                                    {b.status !== "sold" && <Button variant="ghost" size="sm" onClick={() => del(b)} data-testid={`del-bat-${b.id}`}>Del</Button>}
                                </td>
                            </tr>
                            );
                        })}
                        {filtered.length === 0 && <tr><td colSpan={9} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">{total === 0 ? "No batteries yet." : "No batteries match the filter."}</td></tr>}
                    </tbody>
                </table>
                </ZoomScope>
            </Surface>

            {/* Add/Edit */}
            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit battery" : "Add battery"}
                footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-battery">Save</Button></>}>
                <div className="grid grid-cols-2 gap-4">
                    <Input label="Serial #" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value.toUpperCase() })} />
                    <Input label="Brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Exide, Amaron…" />
                    <Input label="Model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
                    <Input label="Voltage" type="number" value={form.voltage} onChange={(e) => setForm({ ...form, voltage: e.target.value })} />
                    <Input label="Capacity (Ah)" type="number" value={form.capacity_ah} onChange={(e) => setForm({ ...form, capacity_ah: e.target.value })} />
                    <Input label="Warranty (months)" type="number" value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: e.target.value })} />
                    <Input label="Date of purchase" type="date"
                        value={form.purchase_date || ""}
                        onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                        data-testid="bat-purchase-date" />
                    <Input label="Expected life (years)" type="number" value={form.expected_life_years} onChange={(e) => setForm({ ...form, expected_life_years: e.target.value })} placeholder="e.g. 3" data-testid="bat-life-years" />
                    <Input label="Purchase cost (₹)" type="number" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} />
                </div>
                {editing && (
                    <div className="mt-4 text-xs text-fleet-inkSoft p-3 bg-slate-50 border border-fleet-border rounded-md">
                        Use <strong>Assign / Switch / Remove</strong> from the table actions to change vehicle assignment — that records full history.
                    </div>
                )}
            </Modal>

            {/* Assign / Switch */}
            <Modal open={!!assignFor} onClose={() => setAssignFor(null)}
                title={assignFor?.status === "fitted" ? `Switch battery ${assignFor.serial_number} to another vehicle` : `Assign battery ${assignFor?.serial_number || ""} to a vehicle`}
                footer={<><Button variant="secondary" onClick={() => setAssignFor(null)}>Cancel</Button><Button onClick={submitAssign} data-testid="confirm-bat-assign">{assignFor?.status === "fitted" ? "Switch" : "Assign"}</Button></>}>
                {assignFor?.status === "fitted" && (
                    <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
                        Currently on <span className="font-mono font-semibold">{vehMap[assignFor.vehicle_id]?.reg_number || "—"}</span>. Assigning to a new vehicle closes that record automatically and computes km run.
                    </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <VehicleSearchSelect
                            vehicles={vehicles}
                            value={assignForm.vehicle_id}
                            onChange={(id) => setAssignForm({ ...assignForm, vehicle_id: id })}
                            label="New vehicle"
                            placeholder="Search by reg #…"
                            filter={(v) => v.id !== assignFor?.vehicle_id}
                            testid="battery-assign-vehicle-search"
                        />
                    </div>

                    {assignFor?.status === "fitted" && (
                        <Input
                            label={`Odometer of present vehicle ${vehMap[assignFor.vehicle_id]?.reg_number ? `(${vehMap[assignFor.vehicle_id].reg_number})` : ""} (km)`}
                            type="number"
                            value={assignForm.odometer_old_vehicle_at_remove}
                            onChange={(e) => setAssignForm({ ...assignForm, odometer_old_vehicle_at_remove: e.target.value })}
                            data-testid="bat-old-odo"
                        />
                    )}
                    <Input
                        label="Odometer of switched vehicle (km)"
                        type="number"
                        value={assignForm.odometer_at_install}
                        onChange={(e) => setAssignForm({ ...assignForm, odometer_at_install: e.target.value })}
                        data-testid="bat-new-odo"
                    />
                    <Input label="Notes" className="col-span-2" value={assignForm.notes}
                        onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} />
                </div>
            </Modal>

            {/* History */}
            <Modal open={!!historyFor} onClose={() => setHistoryFor(null)} size="lg"
                title={`Battery history · ${historyFor?.serial_number || ""}`}>
                {history.length === 0 ? (
                    <div className="text-center py-10 text-sm text-fleet-inkSoft">No assignments yet.</div>
                ) : (
                    <>
                        <div className="flex items-center gap-2 flex-wrap mb-5">
                            {history.map((h, i) => (
                                <span key={h.id} className="inline-flex items-center gap-2">
                                    <span className={`px-2 py-1 border rounded font-mono text-xs ${h.removed_at ? "bg-slate-100 border-fleet-border" : "bg-emerald-50 border-emerald-200"}`}>
                                        {h.vehicle_reg}{!h.removed_at && " · current"}
                                    </span>
                                    {i < history.length - 1 && <ArrowRight size={12} className="text-fleet-inkSoft" />}
                                </span>
                            ))}
                        </div>
                        <table className="w-full text-sm border border-fleet-border rounded-md overflow-hidden">
                            <thead className="bg-slate-50"><tr className="text-left">
                                {["VEHICLE", "ASSIGNED", "REMOVED", "ODO IN", "ODO OUT", "KM RUN", "CUMULATIVE", "REASON", "NOTES"].map((h) => <th key={h} className="label-mono px-3 py-2 font-normal">{h}</th>)}
                            </tr></thead>
                            <tbody>
                                {history.map((h) => (
                                    <tr key={h.id} className="border-t border-fleet-border">
                                        <td className="px-3 py-2 font-mono">{h.vehicle_reg}</td>
                                        <td className="px-3 py-2 font-mono">{formatDate(h.assigned_at)}</td>
                                        <td className="px-3 py-2 font-mono">{h.removed_at ? formatDate(h.removed_at) : "—"}</td>
                                        <td className="px-3 py-2 font-mono">{(h.odometer_at_install || 0).toLocaleString()}</td>
                                        <td className="px-3 py-2 font-mono">{h.odometer_at_remove != null ? h.odometer_at_remove.toLocaleString() : "—"}</td>
                                        <td className="px-3 py-2 font-mono">{(h.km_run || 0).toLocaleString()}</td>
                                        <td className="px-3 py-2 font-mono font-semibold">{(h.cumulative_km || 0).toLocaleString()}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{h.reason || "—"}</td>
                                        <td className="px-3 py-2 text-fleet-inkSoft">{h.notes || ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </Modal>

            {/* Documents (warranty card etc) */}
            <Modal open={!!docsFor} onClose={() => { setDocsFor(null); setDocs([]); }} size="lg"
                title={`Documents · ${docsFor?.serial_number || ""}`}>
                {docsFor && (
                    <div className="space-y-4">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.webp"
                            multiple
                            className="hidden"
                            onChange={async (e) => {
                                const files = Array.from(e.target.files || []);
                                e.target.value = "";
                                if (files.length === 0) return;
                                const lab = window.prompt(
                                    `Label for ${files.length === 1 ? "this document" : `these ${files.length} documents`} (e.g. Warranty Card, Invoice, Photo)`,
                                    "Warranty Card"
                                );
                                if (lab === null) return; // cancelled
                                await uploadDocs(files, (lab || "").trim() || "Document");
                            }}
                            data-testid="bat-upload-doc"
                        />
                        <div className="flex items-center gap-3 flex-wrap">
                            <Button onClick={() => fileInputRef.current?.click()} data-testid="bat-upload-trigger">
                                <UploadSimple size={16} /> Upload document(s)
                            </Button>
                            <span className="text-xs text-fleet-inkSoft">
                                You can select multiple files. PDF / PNG / JPG / WEBP supported.
                            </span>
                        </div>
                        {docs.length === 0 ? (
                            <div className="text-center py-8 text-sm text-fleet-inkSoft border border-dashed border-fleet-border rounded-md">
                                No documents uploaded yet.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {docs.map((d) => (
                                    <div key={d.id} className="border border-fleet-border rounded-md p-3 flex items-start gap-3" data-testid={`bat-doc-${d.id}`}>
                                        <div className="h-9 w-9 rounded-md bg-slate-50 border border-fleet-border flex items-center justify-center text-fleet-inkSoft shrink-0">
                                            <FileText size={16} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate" title={d.original_filename}>{d.original_filename}</div>
                                            <div className="text-xs text-fleet-inkSoft">
                                                {d.label || "Document"} · {Math.round((d.size || 0) / 1024)} KB · {formatDate(d.created_at)}
                                            </div>
                                            <div className="mt-1.5 flex gap-3">
                                                <a
                                                    href={fileUrl(d.id)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs text-fleet-accent hover:underline inline-flex items-center gap-1"
                                                    data-testid={`bat-doc-view-${d.id}`}
                                                >
                                                    <Eye size={12} /> View
                                                </a>
                                                <button
                                                    onClick={() => delDoc(d.id)}
                                                    className="text-xs text-fleet-critical hover:underline inline-flex items-center gap-1"
                                                    data-testid={`bat-doc-delete-${d.id}`}
                                                >
                                                    <Trash size={11} /> Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <Modal
                open={!!sellFor}
                onClose={() => setSellFor(null)}
                title={sellFor ? `${user?.role === "admin" ? "Sell" : "Request sale of"} battery · ${sellFor.serial_number}` : "Sell battery"}
                size="md"
                footer={<>
                    <Button variant="secondary" onClick={() => setSellFor(null)} disabled={sellSubmitting}>Cancel</Button>
                    <Button onClick={submitSell} disabled={sellSubmitting || !sellForm.buyer_name.trim() || !sellForm.sale_amount_inr} data-testid="sell-bat-submit">
                        {sellSubmitting ? "Submitting…" : (user?.role === "admin" ? "Record sale" : "Submit request")}
                    </Button>
                </>}
            >
                {sellFor && (
                    <div className="space-y-3">
                        {sellFor.vehicle_id && (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                This battery is currently assigned to a vehicle. Selling will automatically remove it first.
                            </div>
                        )}
                        <Input label="Buyer name *" value={sellForm.buyer_name} onChange={(e) => setSellForm({ ...sellForm, buyer_name: e.target.value })} data-testid="sell-bat-buyer-name" />
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Sale date" type="date" value={sellForm.sale_date} onChange={(e) => setSellForm({ ...sellForm, sale_date: e.target.value })} />
                            <Input label="Sale amount (₹) *" type="number" min="0" step="0.01" value={sellForm.sale_amount_inr} onChange={(e) => setSellForm({ ...sellForm, sale_amount_inr: e.target.value })} data-testid="sell-bat-amount" />
                        </div>
                        <Input label="Buyer phone (optional)" value={sellForm.buyer_phone} onChange={(e) => setSellForm({ ...sellForm, buyer_phone: e.target.value })} />
                        <div>
                            <label className="label-mono text-xs text-fleet-inkSoft mb-1 block">Buyer address (optional)</label>
                            <textarea rows={2} value={sellForm.buyer_address} onChange={(e) => setSellForm({ ...sellForm, buyer_address: e.target.value })} className="w-full border border-fleet-border rounded-md px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="label-mono text-xs text-fleet-inkSoft mb-1 block">Notes / remarks (optional)</label>
                            <textarea rows={2} value={sellForm.notes} onChange={(e) => setSellForm({ ...sellForm, notes: e.target.value })} className="w-full border border-fleet-border rounded-md px-3 py-2 text-sm" />
                        </div>
                    </div>
                )}
            </Modal>

        </div>
    );
}
